import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  PageContent,
  openNotificationWithIcon,
  Segment,
  useUserinfo,
  PermissionGuard,
} from '@mss/mss-component-library';
import { Collapse, notification } from 'antd';
import Api from '../../../services/Api';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';
import {
  permissions,
  PermissionSetOption,
} from '../../../util/permissionsForEdit';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Cell, RowHeader } from 'jsx/authorization/components/Table';
import PageContainer from 'jsx/authorization/components/PageContainer';
import GroupPermissionRow from 'jsx/authorization/components/GroupPermissionRow';
import { AppContext } from 'jsx';
import { useLicensesQuery } from '../../../../asset/services/queries';

type Props = {
  group: any;
  superAdminGroup: boolean;
  groupPermissions: (number | undefined)[];
  fetchGroupPermissions: () => any;
  checkedList: (number | undefined)[];
  setCheckedList: Dispatch<SetStateAction<(number | undefined)[]>>;
  groupLoading: boolean;
};
const CHECKBOX_COLUMN_WIDTH = 200;

export default function Permissions({
  group,
  superAdminGroup,
  groupPermissions,
  fetchGroupPermissions,
  checkedList,
  setCheckedList,
  groupLoading,
}: Props) {
  const [hasPermission] = useUserinfo();
  const { allPermissionsMap, permissionsWithView, invertedAllPermissionsMap } =
    useContext(AppContext);
  const { data: license } = useLicensesQuery();
  const { groupId } = useParams<{ groupId: string }>();
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const [t] = useTranslation(['authorization', 'common']);

  let optionsWithViewPermission: PermissionSetOption[] = [];
  permissionsWithView.forEach(option => {
    const permissionId = allPermissionsMap.get(option.codename!);
    const found = checkedList.find(item => item === permissionId);
    if (found && option.view) {
      optionsWithViewPermission.push(option);
    }
  });

  const licensedPermissionsCodenames = permissionsWithView
    .filter(
      perm =>
        !!perm.license_codename && license && perm.license_codename in license
    )
    .map(el => el.codename);

  const handleChange = async (
    e: CheckboxChangeEvent,
    groupPermissions: number[],
    permission: number,
    permissionObject: PermissionSetOption
  ) => {
    setIsFetching(true);
    const enable = e.target.checked;
    // Add view permission on Create / Update / Delete
    let viewPermissionId: (number | undefined)[] = [];

    if (permissionObject.view) {
      if (Array.isArray(permissionObject.view)) {
        permissionObject.view.forEach((viewCodename: string) => {
          viewPermissionId.push(allPermissionsMap.get(viewCodename));
        });
      } else {
        viewPermissionId = [allPermissionsMap.get(permissionObject.view)];
      }
    }

    let newPermissions: (number | undefined)[] = [];
    if (enable) {
      newPermissions = viewPermissionId.length
        ? [permission, ...viewPermissionId, ...groupPermissions]
        : [permission, ...groupPermissions];
    }
    if (!enable) {
      newPermissions =
        groupPermissions?.filter((x: number) => x !== permission) || [];
    }

    const resp = await Api.group.updatePermissions({
      ...group,
      permissions: newPermissions,
    });
    if (resp.status >= 200 && resp.status < 300) {
      fetchGroupPermissions();
      openNotificationWithIcon(
        enable ? notification.success : notification.warning,
        enable ? t(`Permission added`) : t('Permission removed'),
        '',
        5
      );
      setIsFetching(false);
    } else {
      openNotificationWithIcon(
        notification.error,
        t('Changing permissions failed'),
        t(
          'The permission could not be changed for this group. Please try again later.'
        )
      );
      setIsFetching(false);
    }
  };

  const handleCheckAll = async (
    e: CheckboxChangeEvent,
    permissions: number[]
  ) => {
    setIsFetching(true);
    const enable = e.target.checked;
    const isViewOrViewSite =
      e.target.name === 'View' || e.target.name === 'View site';

    // View permissions to autoselect when 'Check All'
    let viewPermissions: number[] = [];
    permissions.forEach((code: number) => {
      const foundPermObj = permissionsWithView.find(
        permObj => allPermissionsMap.get(permObj.codename!) === code
      );
      if (foundPermObj && foundPermObj.view) {
        const viewPermission = Array.isArray(foundPermObj.view)
          ? // Parse in number code
            [...foundPermObj.view].map(code => allPermissionsMap.get(code)!)
          : [foundPermObj.view].map(code => allPermissionsMap.get(code)!);
        viewPermissions.push(...viewPermission);
      }
    });

    let newPermissions: (number | undefined)[] = [];
    if (enable) {
      setCheckedList([...checkedList, ...permissions, ...viewPermissions]);
      newPermissions = [
        ...permissions,
        ...viewPermissions,
        ...groupPermissions,
      ];
    }
    if (!enable) {
      if (isViewOrViewSite) {
        // This section is way too much magic and should be consolidated in the configuration and/or permission mapping
        const currentlyCheckPermissionNames = checkedList.reduce(
          (acc: string[], perm) => {
            const name = invertedAllPermissionsMap.get(perm!);
            if (name) return [...acc, name];
            return acc;
          },
          []
        );
        const permissionNames = permissions.reduce((acc: string[], perm) => {
          const name = invertedAllPermissionsMap.get(perm);
          if (name)
            return [
              ...acc,
              name
                .replace('admin_view_', '')
                .replace('view_', '')
                .replace('observer_', ''),
            ];
          return acc;
        }, []);

        const filteredCCPN = currentlyCheckPermissionNames.filter(
          ccpn =>
            !ccpn.startsWith('view_') &&
            !ccpn.startsWith('view_center') &&
            !ccpn.startsWith('observer') &&
            !ccpn.startsWith('admin_view') &&
            !ccpn.startsWith('create') &&
            !ccpn.startsWith('start')
        );
        const viewPermissionWithoutRelatedPermissions = permissionNames.reduce(
          (acc: number[], permissionName: string) => {
            if (!filteredCCPN.find(x => x.includes(permissionName))) {
              const viewPermissionCode = allPermissionsMap.get(
                `view_${permissionName}`
              );
              const observerPermissionCode = allPermissionsMap.get(
                `observer_${permissionName}`
              );
              const adminViewPermissionCode = allPermissionsMap.get(
                `admin_view_${permissionName}`
              );
              return [
                ...acc,
                ...(viewPermissionCode ? [viewPermissionCode] : []),
                ...(observerPermissionCode ? [observerPermissionCode] : []),
                ...(adminViewPermissionCode ? [adminViewPermissionCode] : []),
              ];
            }
            return acc;
          },
          []
        );

        newPermissions = checkedList.filter(
          each => !viewPermissionWithoutRelatedPermissions.includes(each!)
        );
      } else {
        newPermissions = checkedList.filter(
          each => !permissions.includes(each!)
        );
      }
      setCheckedList([...newPermissions]);
    }
    if (newPermissions.length === checkedList.length) {
      setIsFetching(false);
      openNotificationWithIcon(
        notification.info,
        t('No permissions to remove'),
        '',
        5
      );
      return;
    }

    const resp = await Api.group.updatePermissions({
      ...group,
      permissions: newPermissions,
    });

    if (resp.status >= 200 && resp.status < 300) {
      fetchGroupPermissions();
      openNotificationWithIcon(
        enable ? notification.success : notification.warning,
        enable ? t(`Permission added`) : t('Permission removed'),
        '',
        5
      );
      setIsFetching(false);
    } else {
      openNotificationWithIcon(
        notification.error,
        t('Changing permissions failed'),
        t(
          'The permission could not be changed for this group. Please try again later.'
        )
      );
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (!!groupId) fetchGroupPermissions();
    // eslint-disable-next-line
  }, [groupId]);

  const hasIds = allPermissionsMap.size > 0 && groupPermissions != null;
  const isDisabled =
    !hasIds ||
    groupLoading ||
    !hasPermission(['change_grouppermission', 'delete_grouppermission']) ||
    superAdminGroup;

  const defaultActiveKey = permissions(t).map(p => p.label);

  return (
    <PermissionGuard
      redirect
      codename={['change_grouppermission', 'view_grouppermission']}
    >
      <PageContainer>
        <PageContent title={t('Permissions')}>
          {!!groupId && hasIds ? (
            <Collapse
              defaultActiveKey={defaultActiveKey}
              expandIconPosition="end"
            >
              {permissions(t).map(permissionsSet => {
                return (
                  <Collapse.Panel
                    header={permissionsSet.label}
                    key={permissionsSet.label}
                    data-ta={`permissionSet-${permissionsSet.label}`}
                  >
                    <Segment>
                      <RowHeader>
                        <Cell style={{ paddingLeft: 16 }}>
                          {t('Permission')}
                        </Cell>
                        {permissionsSet.columns.map(column => {
                          const columnPermissions =
                            column.column_permissions?.map(
                              codename => allPermissionsMap.get(codename)!
                            );
                          const checked = columnPermissions?.every(code =>
                            checkedList.includes(code)
                          );

                          const indeterminate =
                            checkedList.some(code =>
                              columnPermissions?.includes(code!)
                            ) && !checked;

                          // If any permission in column is not included in license - disable check all
                          const checkAllLicenseDisabled =
                            column.column_permissions?.some(
                              codename =>
                                !licensedPermissionsCodenames.includes(codename)
                            );

                          return (
                            <Cell
                              key={column.label}
                              data-ta="permissionTypeHeader"
                              width={CHECKBOX_COLUMN_WIDTH}
                              style={{
                                justifyContent: 'center',
                                textAlign: 'center',
                              }}
                            >
                              {column.label}
                              {columnPermissions && (
                                <Checkbox
                                  name={column.label}
                                  className="ml-1"
                                  checked={checked}
                                  indeterminate={indeterminate}
                                  disabled={
                                    isFetching ||
                                    isDisabled ||
                                    checkAllLicenseDisabled
                                  }
                                  onChange={e =>
                                    handleCheckAll(e, columnPermissions)
                                  }
                                />
                              )}
                            </Cell>
                          );
                        })}
                      </RowHeader>
                      {!!license &&
                        permissionsSet.permissions.map(perSet => (
                          <GroupPermissionRow
                            key={perSet.label}
                            perSet={perSet}
                            groupPermissions={groupPermissions}
                            checkedList={checkedList}
                            setCheckedList={setCheckedList}
                            optionsWithViewPermission={
                              optionsWithViewPermission
                            }
                            disabled={isDisabled || isFetching}
                            license={license}
                            handleChange={handleChange}
                          />
                        ))}
                    </Segment>
                  </Collapse.Panel>
                );
              })}
            </Collapse>
          ) : null}
        </PageContent>
      </PageContainer>
    </PermissionGuard>
  );
}
